import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import { Button } from '@/shared/components/ui';
import { extractApiError } from '@/shared/lib/extractApiError';
import { formatBookingClock } from '@/shared/utils/bookingWhen';
import type { Booking } from '@/types';
import { useBookingExtensions } from '../hooks/useTripExtensions';
import { formatExtensionMoney, isExtendableStatus } from '../utils/extensionDisplay';
import { ExtensionStatusCard } from './ExtensionStatusCard';
import { ExtraTimeDialog } from './ExtraTimeDialog';

interface TripExtensionPanelProps {
  booking: Pick<
    Booking,
    'id' | 'status' | 'startAt' | 'endAt' | 'originalEndAt' | 'extendedHours' | 'currency'
  >;
  viewer: 'traveler' | 'guide';
  /** Open the request dialog on mount (deep link from a booking card). */
  autoOpen?: boolean;
  readOnly?: boolean;
}

/** Trip time, extra-time requests and their history for one booking. */
export function TripExtensionPanel({ booking, viewer, autoOpen, readOnly }: TripExtensionPanelProps) {
  const extensionsQuery = useBookingExtensions(booking.id);
  const [dialogOpen, setDialogOpen] = useState(false);

  const extensions = extensionsQuery.data ?? [];
  const open = extensions.find((ext) => ext.isOpen);
  const history = extensions.filter((ext) => !ext.isOpen);
  const paid = history.filter((ext) => ext.status === 'PAID');
  const canRequest =
    !readOnly &&
    isExtendableStatus(booking.status) &&
    Boolean(booking.endAt) &&
    new Date(booking.endAt!).getTime() > Date.now() &&
    !open;

  useEffect(() => {
    if (autoOpen && canRequest) setDialogOpen(true);
    // Only on first eligibility, not every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, canRequest && extensionsQuery.isSuccess]);

  // Nothing to show for trips that can't be extended and never were.
  if (!isExtendableStatus(booking.status) && extensions.length === 0 && !extensionsQuery.isLoading) {
    return null;
  }

  const extendedHours = booking.extendedHours ?? 0;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Trip time
        </Typography>
        {canRequest && (
          <Button
            intent="secondary-outlined"
            size="small"
            startIcon={<AddAlarmRoundedIcon fontSize="small" />}
            onClick={() => setDialogOpen(true)}
          >
            {viewer === 'guide' ? 'Suggest extra time' : 'Add extra time'}
          </Button>
        )}
      </Stack>

      {booking.startAt && booking.endAt && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {formatBookingClock(booking.startAt)} – {formatBookingClock(booking.endAt)}
          {extendedHours > 0 && booking.originalEndAt
            ? ` (originally until ${formatBookingClock(booking.originalEndAt)}, +${extendedHours} h added)`
            : ''}
        </Typography>
      )}

      {extensionsQuery.isLoading && <Skeleton variant="rounded" height={72} />}
      {extensionsQuery.isError && (
        <Alert severity="error">{extractApiError(extensionsQuery.error).message}</Alert>
      )}

      <Stack spacing={1.5}>
        {open && <ExtensionStatusCard extension={open} viewer={viewer} />}

        {history.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Extra-time history
              {paid.length > 0 &&
                ` · ${formatExtensionMoney(
                  paid.reduce((sum, ext) => sum + ext.totalAmount, 0),
                  booking.currency
                )} paid separately`}
            </Typography>
            <Stack spacing={1}>
              {history.map((ext) => (
                <ExtensionStatusCard key={ext.id} extension={ext} viewer={viewer} />
              ))}
            </Stack>
          </Box>
        )}

        {!open && history.length === 0 && extensionsQuery.isSuccess && canRequest && (
          <Typography variant="body2" color="text.secondary">
            {viewer === 'guide'
              ? 'Running long? Suggest extra whole hours; the traveler approves and pays online.'
              : 'Want more time with your guide? Add whole hours, paid online once your guide accepts.'}
          </Typography>
        )}
      </Stack>

      <ExtraTimeDialog
        bookingId={booking.id}
        open={dialogOpen}
        viewer={viewer}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
